import type {
  CaseStep,
  CaseType,
} from '@repo/internal-tools-api/src/services/cases/types.ts'
import type {
  CheckboxGroupOption,
  CheckboxGroupValue,
} from '@repo/internal-tools-design-system/src/lib/CheckboxGroup/CheckboxGroup.tsx'

const CASE_STEPS_BY_CASE_TYPE: Record<CaseType, CaseStep[]> = {
  CardDispute: [
    'Triage',
    'Investigation',
    'PendingCMResponse',
    'InChargeback',
    'SentToEngineering',
    'Resolution',
  ],
  CreditReportDispute: [
    'Triage',
    'Investigation',
    'PendingCMResponse',
    'SentToEngineering',
    'Resolution',
  ],
  MisdirectedDeposit: ['Triage', 'PendingCMResponse', 'Resolution'],
  FinCrimes: [
    'Triage',
    'Investigation',
    'CaseDiscussion',
    'Monitoring',
    'ReferToPartner',
    'Resolution',
  ],
  ProofOfAuth: ['SearchCM', 'SelectTransactions', 'Resolution'],
  VisaRiskManager: [
    'Triage',
    'Investigation',
    'PendingCMResponse',
    'Resolution',
  ],
}

export const CLOSE_CASE_STEP_FILTER_VALUES: CaseStep[] = ['Archived', 'Closed']

export const ALL_CASE_STEP_FILTER_VALUES: CaseStep[] = [
  'Triage',
  'Investigation',
  'PendingCMResponse',
  'InChargeback',
  'SentToEngineering',
  'Resolution',
  'CaseDiscussion',
  'Monitoring',
  'ReferToPartner',
  'Archived',
  'Closed',
]

/**
 * Get unique open case steps based on case types
 * @param caseTypes - Array of case types for the user
 * @returns Array of unique case steps for the case types
 */
export const getOpenCaseStepsForCaseTypes = (
  caseTypes: CaseType[],
): CaseStep[] => {
  const stepsSet = new Set<CaseStep>()

  caseTypes.forEach((caseType) => {
    const steps = CASE_STEPS_BY_CASE_TYPE[caseType]
    if (steps) {
      steps.forEach((step) => stepsSet.add(step))
    }
  })

  return Array.from(stepsSet)
}

export type CaseStepsFilterOptionValue = CaseStep

export type CaseStepsFilter = CheckboxGroupValue<CaseStepsFilterOptionValue>

/**
 * Generate filter options for open case steps
 */
export const getOpenCaseStepFilterOptions = (
  caseTypes: CaseType[],
): CheckboxGroupOption<CaseStepsFilterOptionValue>[] => {
  const openSteps = getOpenCaseStepsForCaseTypes(caseTypes)
  return openSteps.map((step) => ({
    id: step,
    label: step,
    value: step,
    'data-testid': `checkbox-group-item-${step}`,
  }))
}

/**
 * Generate filter options for close case steps (constant for all case types)
 */
export const CLOSE_CASE_STEP_FILTER_OPTIONS: CheckboxGroupOption<CaseStepsFilterOptionValue>[] =
  CLOSE_CASE_STEP_FILTER_VALUES.map((step) => ({
    id: step,
    label: step,
    value: step,
    'data-testid': `checkbox-group-item-${step}`,
  }))

/**
 * Get default filter values based on case types
 */
export const getDefaultCaseStepFilterValues = (
  caseTypes: CaseType[],
): CaseStepsFilterOptionValue[] => {
  const openSteps = getOpenCaseStepsForCaseTypes(caseTypes)
  // Include all open steps and 'Closed' by default
  return [...openSteps, 'Closed']
}
